<template>
    <div class="h-full w-full flex flex-col p-8">
        <div class="h-full flex justify-center items-center" v-if="$apollo.queries.application.loading">
            <base-spinner></base-spinner></div>
        <div v-else class="h-full w-full flex flex-col">
            <div class="w-full flex justify-start items-center shadow-lg rounded-2xl p-2 sm:p-4 relative"
                :style="getBackgroundImage">
                <div class="h-12 sm:h-16 lg:h-20 w-12 sm:w-16 lg:w-20 mr-2 sm:mr-4 flex-shrink-0">
                    <img class="h-full w-full object-cover rounded-xl" :src="getProfilePicture" alt="avatar">
                </div>
                <div class="flex-grow flex flex-col">
                    <h1 class="text-base sm:text-lg lg:text-xl font-medium capitalize break-all">{{ application.name_en
                        }}</h1>
                    <p class="text-xs lg:text-sm text-gray-600 break-all">{{ application.primary_email }}</p>
                </div>
            </div>
            <div class="mt-8 w-full flex justify-start items-center flex-wrap relative bg-transparent rounded-xl">
                <div v-for="(tab, index) in tabs" :key="index" class="px-4 cursor-pointer transition-all"
                    :class="activeTab == tab.id ? 'text-ternary font-medium' : 'text-primary'" :id="tab.id"
                    @click="selectTab(tab.id)">{{ tab.name }}</div>
                <div :style="getActiveTabStyle"
                    class="active-tab z-10 absolute rounded-lg bg-inherit flex-auto text-center bg-none border-0 block">
                </div>
            </div>
            <div class="grid grid-col-1 gap-4 mt-8">
                <transition name="fade" mode="out-in">
                    <div v-if="activeTab == 'personal-information'" class="w-full flex flex-col border rounded-2xl p-4">
                        <div class="flex text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold capitalize w-40">English Name</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{ application.name_en }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Arabic Name</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{ application.name_ar }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Emirates ID</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{ application.eid }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Nationality</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{ application.nationality }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Gender</div>
                            <div class="text-gray-600 capitalize"><span class="mr-4">:</span>{{ application.gender ?
                                application.gender == 'M' ? 'Male' : 'Female' : '' }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Date Of Birth</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{
                                formattedDate(application.birthdate) }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Expire EID</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{
                                formattedDate(application.eid_expiry_date) }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Primary Phone</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{ application.primary_phone }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Secondary Phone</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{ application.secondary_phone }}
                            </div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Personal Email</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{ application.secondary_email }}
                            </div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Marital Status</div>
                            <div class="text-gray-600 capitalize"><span class="mr-4">:</span>{{
                                getMaritalStatus(application.marital_status) }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Emirate</div>
                            <div class="text-gray-600 capitalize"><span class="mr-4">:</span>{{ application.emirate }}
                            </div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">City</div>
                            <div class="text-gray-600 capitalize"><span class="mr-4">:</span>{{ application.city }}
                            </div>
                        </div>
                    </div>
                    <div v-else-if="activeTab == 'academic-information'"
                        class="w-full flex flex-col border rounded-2xl p-4">
                        <div>
                            <h1 class="font-medium text-xs sm:text-sm lg:text-base text-secondary">High School Details
                            </h1>
                            <div class="flex mt-3 text-xs sm:text-sm lg:text-base">
                                <div class="font-semibold w-40">High School Name</div>
                                <div class="text-gray-600"><span class="mr-4">:</span>{{ getHighSchoolName }}</div>
                            </div>
                            <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                                <div class="font-semibold w-40">Average</div>
                                <div class="text-gray-600"><span class="mr-4">:</span>{{ getHighSchoolAverage }}</div>
                            </div>
                            <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                                <div class="font-semibold w-40">Stream</div>
                                <div class="text-gray-600"><span class="mr-4">:</span>{{ getHighSchoolStream }}</div>
                            </div>
                            <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                                <div class="font-semibold w-40">Graduation Year</div>
                                <div class="text-gray-600"><span class="mr-4">:</span>{{ getHighSchoolGraduationYear }}
                                </div>
                            </div>
                            <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                                <div class="font-semibold w-40">Location</div>
                                <div class="text-gray-600"><span class="mr-4">:</span>{{ getHighSchoolLocation }}</div>
                            </div>
                        </div>
                        <div v-if="showUniversityDetailsView" class="mt-4">
                            <h1 class="font-medium text-xs sm:text-sm lg:text-base text-secondary">University Details
                            </h1>
                            <div class="flex mt-3 text-xs sm:text-sm lg:text-base">
                                <div class="font-semibold w-40">College Name</div>
                                <div class="text-gray-600"><span class="mr-4">:</span>{{ getCollegeName }}</div>
                            </div>
                            <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                                <div class="font-semibold w-40">Location</div>
                                <div class="text-gray-600"><span class="mr-4">:</span>{{ getCollegeLocation }}</div>
                            </div>
                            <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                                <div class="font-semibold w-40">Year Of Starting</div>
                                <div class="text-gray-600"><span class="mr-4">:</span>{{ getCollegeYearOfStarting }}
                                </div>
                            </div>
                            <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                                <div class="font-semibold w-40">Graduation Year</div>
                                <div class="text-gray-600"><span class="mr-4">:</span>{{ getCollegeGraduationYear }}
                                </div>
                            </div>
                            <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                                <div class="font-semibold w-40">GPA</div>
                                <div class="text-gray-600"><span class="mr-4">:</span>{{ getCollegeGpa }}</div>
                            </div>
                            <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                                <div class="font-semibold w-40">Academic Major(s)</div>
                                <div class="text-gray-600"><span class="mr-4">:</span>{{ getCollegeAcademicMajors }}
                                </div>
                            </div>
                            <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                                <div class="font-semibold w-40">Sponsor</div>
                                <div class="text-gray-600"><span class="mr-4">:</span>{{ getCollegeSponsor }}</div>
                            </div>
                        </div>

                    </div>
                    <div v-else class="w-full flex flex-col border rounded-2xl p-4">
                        <div class="flex text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Employer</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{
                                application.employment_informations.employer }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Sector</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{
                                application.employment_informations.sector }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Location</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{
                                application.employment_informations.location }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Position</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{
                                application.employment_informations.position }}</div>
                        </div>
                        <div class="flex mt-1 text-xs sm:text-sm lg:text-base">
                            <div class="font-semibold w-40">Salary Range</div>
                            <div class="text-gray-600"><span class="mr-4">:</span>{{
                                application.employment_informations.salary_range }}</div>
                        </div>
                    </div>
                </transition>
            </div>

            <div v-if="role == 'careercounselor'" class="flex justify-center items-center mt-4">
                <div class="mr-4">
                    <div class="flex justify-end items-center">
                        <div v-if="isApproving" style="height: 38px;"
                            class="w-32 flex items-center justify-center hover:bg-white bg-green-600 border-green-600 hover:border-green-600 border px-4 py-2 rounded-lg hover:text-green-600 text-white">
                            <base-spinner-with-text class-list="w-4 h-4" text=""
                                text-class-list="text-white"></base-spinner-with-text>
                            <span class="text-xs">Approving...</span>
                        </div>
                        <div v-else style="height: 38px;" @click="updateApplication(true)"
                            class="w-32 flex items-center justify-center hover:bg-white bg-green-600 border-green-600 hover:border-green-600 border px-4 py-2 rounded-lg cursor-pointer hover:text-green-600 text-white">
                            <icon-check class="w-4 h-4"></icon-check>
                            <span class="text-xs ml-2">Approve</span>
                        </div>
                    </div>
                </div>
                <div class="ml-4">
                    <div class="flex justify-end items-center">
                        <div v-if="isRejecting" style="height: 38px;"
                            class="w-32 flex items-center justify-center hover:bg-white bg-red-600 border-red-600 hover:border-red-600 border px-4 py-2 rounded-lg hover:text-red-600 text-white">
                            <base-spinner-with-text class-list="w-4 h-4" text=""
                                text-class-list="text-white"></base-spinner-with-text>
                            <span class="text-xs">Rejecting...</span>
                        </div>
                        <div v-else style="height: 38px;" @click="updateApplication(false)"
                            class="w-32 flex items-center justify-center hover:bg-white bg-red-600 border-red-600 hover:border-red-600 border px-4 py-2 rounded-lg cursor-pointer hover:text-red-600 text-white">
                            <icon-close class="w-4 h-4"></icon-close>
                            <span class="text-xs ml-2">Reject</span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="role == 'supervisor'" class="flex justify-center items-center mt-4">
                <div class="flex justify-end items-center">
                    <div v-if="isConfirming" style="height: 38px;"
                        class="w-32 flex items-center justify-center hover:bg-white bg-green-600 border-green-600 hover:border-green-600 border px-4 py-2 rounded-lg hover:text-green-600 text-white">
                        <base-spinner-with-text class-list="w-4 h-4" text=""
                            text-class-list="text-white"></base-spinner-with-text>
                        <span class="text-xs">Confirming...</span>
                    </div>
                    <div v-else style="height: 38px;" @click="confirmBySupervisor(true)"
                        class="w-32 flex items-center justify-center hover:bg-white bg-green-600 border-green-600 hover:border-green-600 border px-4 py-2 rounded-lg cursor-pointer hover:text-green-600 text-white">
                        <icon-check class="w-4 h-4"></icon-check>
                        <span class="text-xs ml-2">Confirm</span>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import IconCheck from '../../components/icons/IconCheck.vue';
import IconClose from '../../components/icons/IconClose.vue';
import backgroundImage from '../../assets/images/profile/bg.jpeg';
export default {
    name: "student-view",
    emits: ["student-verified"],
    props: ["studentId"],
    components: {
        IconCheck,
        IconClose,
    },
    data() {
        return {
            isLoading: false,
            error: null,
            application: null,
            tabs: [
                { id: 'personal-information', name: 'Personal Information' },
                { id: 'academic-information', name: 'Academic Information' },
            ],
            activeTab: 'personal-information',
            indicatorOffset: 0,
            indicatorWidth: 181.359375,
            isApproving: false,
            isRejecting: false,
            isConfirming: false,
        };
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        getBackgroundImage() {
            return {
                'background-image': `url(${backgroundImage})`
            }
        },
        getProfilePicture() {
            return `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${this.studentId}/${this.application.entity}`;
        },
        getActiveTabStyle() {
            return {
                'width': `${this.indicatorWidth}px`,
                'transform': `translate3d(${this.indicatorOffset}px, 0px, 0px)`
            }
        },
        getOrganizationType() {
            if (this.application.entity == 'ATHS' || this.application.entity == 'STS') {
                return "School";
            } else {
                return "College";
            }
        },
        getHighSchoolName() {
            if (this.application.high_school_details) {
                return this.application.high_school_details.name;
            } else {
                if (this.getOrganizationType == 'College') {
                    return "";
                } else {
                    return this.application.campus;
                }
            }
        },
        getHighSchoolAverage() {
            if (this.application.high_school_details) {
                return this.application.high_school_details.average;
            } else {
                if (this.getOrganizationType == 'College') {
                    return "";
                } else {
                    return this.application.gpa;
                }
            }
        },
        getHighSchoolStream() {
            if (this.application.high_school_details) {
                return this.application.high_school_details.stream;
            } else {
                if (this.getOrganizationType == 'College') {
                    return "";
                } else {
                    return this.application.study_program;
                }
            }
        },
        getHighSchoolGraduationYear() {
            if (this.application.high_school_details) {
                return this.application.high_school_details.graduation_year;
            } else {
                if (this.getOrganizationType == 'College') {
                    return "";
                } else {
                    return this.application.grad_year;
                }
            }
        },
        getHighSchoolLocation() {
            if (this.application.high_school_details) {
                return this.application.high_school_details.location;
            } else {
                if (this.getOrganizationType == 'College') {
                    return "";
                } else {
                    return this.application.location;
                }
            }
        },
        getCollegeName() {
            if (this.application.university_details) {
                return this.application.university_details.name;
            } else {
                if (this.getOrganizationType == 'School') {
                    return "";
                } else {
                    return this.application.campus;
                }
            }
        },
        getCollegeLocation() {
            if (this.application.university_details) {
                return this.application.university_details.location;
            } else {
                if (this.getOrganizationType == 'School') {
                    return "";
                } else {
                    return this.application.location;
                }
            }
        },
        getCollegeYearOfStarting() {
            if (this.application.university_details) {
                return this.application.university_details.year_of_starting;
            } else {
                if (this.getOrganizationType == 'School') {
                    return "";
                } else {
                    return "";
                }
            }
        },
        getCollegeGraduationYear() {
            if (this.application.university_details) {
                return this.application.university_details.graduation_year;
            } else {
                if (this.getOrganizationType == 'School') {
                    return "";
                } else {
                    return this.application.grad_year;
                }
            }
        },
        getCollegeAcademicMajors() {
            if (this.application.university_details) {
                return this.application.university_details.academic_majors;
            } else {
                if (this.getOrganizationType == 'School') {
                    return "";
                } else {
                    return this.application.study_program;
                }
            }
        },
        getCollegeGpa() {
            if (this.application.university_details) {
                return this.application.university_details.gpa;
            } else {
                if (this.getOrganizationType == 'School') {
                    return "";
                } else {
                    return this.application.gpa;
                }
            }
        },
        getCollegeSponsor() {
            if (this.application.university_details) {
                return this.application.university_details.sponsor ? 'Yes' : 'No';
            } else {
                return "";
            }
        },
        showUniversityDetailsView() {
            if (this.getOrganizationType == 'School') {
                if (this.application.student_status == 'AS') {
                    return false;
                } else {
                    return true;
                }
            } else {
                return true;
            }
        },
    },
    watch: {
        activeTab() {
            const activeTabBtn = document.getElementById(`${this.activeTab}`);
            if (activeTabBtn) {
                this.indicatorOffset = activeTabBtn.offsetLeft;
                this.indicatorWidth = activeTabBtn.getBoundingClientRect().width;
            }
        },
        application() {
            if (this.application.student_status == 'IG' && this.application.is_employed) {
                this.tabs.push({ id: 'employment-information', name: 'Employment Information' });
            }
        },
    },
    mounted() {
        let $this = this;
        setTimeout(() => {
            const activeTabBtn = document.getElementById(`${$this.activeTab}`);
            if (activeTabBtn) {
                $this.indicatorOffset = activeTabBtn.offsetLeft;
                $this.indicatorWidth = activeTabBtn.getBoundingClientRect().width;
            }
        }, 1000);
    },
    apollo: {
        application() {
            return {
                query: gql`query GetApplicationDetails($student_id: String!) {
                    application(student_id: $student_id) {
                        student_id
                        status
                        student_status
                        name_en
                        name_ar
                        entity
                        marital_status
                        campus
                        study_program
                        gpa
                        grad_year
                        grad_date
                        gender
                        birthdate
                        nationality
                        nationality_type
                        mother_natl_code
                        primary_phone
                        secondary_phone
                        eid
                        primary_email
                        secondary_email
                        notes
                        eid_expiry_date
                        emirate
                        city
                        location
                        is_employed
                        high_school_details {
                            name
                            average
                            stream
                            graduation_year
                            location
                        }
                        university_details {
                            name
                            location
                            year_of_starting
                            graduation_year
                            academic_majors
                            gpa
                            sponsor
                        }
                        employment_informations {
                            employer
                            sector
                            location
                            position
                            salary_range
                        }
                    }
                }`,
                variables() {
                    return {
                        student_id: this.studentId,
                    }
                },
                fetchPolicy: 'cache-and-network'
            }
        },
    },
    methods: {
        async confirmBySupervisor(status) {
            this.isConfirming = true;

            const CONFIRM_BY_SUPERVISOR_MUTATION = gql`
                mutation ConfirmBySupervisor($student_id: String!, $status: Boolean!) {
                    confirmBySupervisor(student_id: $student_id, status: $status) {
                        error
                        message
                    }
                }
            `;

            try {
                await this.$apollo.mutate({
                    mutation: CONFIRM_BY_SUPERVISOR_MUTATION,
                    variables: {
                        student_id: this.studentId,
                        status: status,
                    }
                }).then((data) => {
                    if (data.data.confirmBySupervisor.error) {
                        this.error = data.data.confirmBySupervisor.message;
                    } else {
                        this.success = data.data.confirmBySupervisor.message;
                    }
                }).catch((error) => {
                    console.error(error)
                })
            } catch (error) {
                this.error = error.message || "Failed to authenticate, try later.";
            }

            this.isConfirming = false;

            if (this.error === null) {
                this.$emit('student-verified', status);
            }

        },
        async updateApplication(status) {

            if (status) {
                this.isApproving = true;
            } else {
                this.isRejecting = true;
            }

            const UPDATE_APPLICATION_MUTATION = gql`
                mutation UpdateApplication($student_id: String!, $status: Boolean!) {
                    updateApplication(student_id: $student_id, status: $status) {
                        error
                        message
                    }
                }
            `;

            try {
                await this.$apollo.mutate({
                    mutation: UPDATE_APPLICATION_MUTATION,
                    variables: {
                        student_id: this.studentId,
                        status: status,
                    }
                }).then((data) => {
                    if (data.data.updateApplication.error) {
                        this.error = data.data.updateApplication.message;
                    } else {
                        this.success = data.data.updateApplication.message;
                    }
                }).catch((error) => {
                    console.error(error)
                })
            } catch (error) {
                this.error = error.message || "Failed to authenticate, try later.";
            }

            if (status) {
                this.isApproving = false;
            } else {
                this.isRejecting = false;
            }

            if (this.error === null) {
                this.$emit('student-verified', status);
            }

        },
        formattedDate(date) {
            if (date) {
                const parsedDate = new Date(date);
                return parsedDate.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                }).replace(/\//g, '-');
            } else {
                return "";
            }
        },
        selectTab(selectedTab) {
            this.activeTab = selectedTab;
        },
        getMaritalStatus(status) {
            if (status == 'S') {
                return "Single";
            } else if (status == 'M') {
                return "Married";
            } else if (status == 'W') {
                return "Widowed";
            } else if (status == 'D') {
                return "Divorced";
            } else if (status == 'U') {
                return "Unmarried";
            } else {
                return "";
            }
        },
        getOrgType(entity) {
            if (entity == 'ATHS' || entity == 'STS') {
                return "School";
            } else {
                return "College";
            }
        },
        getStudentStatusclass(status) {
            switch (status) {
                case 'AS':
                    return 'text-green-600';
                case 'DA':
                    return 'text-red-500';
                case 'DB':
                    return 'text-red-600';
                case 'DF':
                    return 'text-blue-600';
                case 'DN':
                    return 'text-blue-400';
                case 'GR':
                    return 'text-yellow-400';
                case 'IG':
                    return 'text-yellow-600';
                case 'IS':
                    return 'text-yellow-500';
                case 'NS':
                    return 'text-red-400';
                case 'SA':
                    return 'text-red-700';
                case 'SB':
                    return 'text-red-400';
                case 'WD':
                    return 'text-red-400';
                case 'WS':
                    return 'text-red-400';
                default:
                    return '';
            }
        },
        replaceSemiColumn() {
            //.replace(/;/g, ', ')
        },
    },
}
</script>

<style lang="scss" scoped>
.bg-1 {
    background: linear-gradient(180deg, #fdd7ff 0%, white 100%);
}

.active-tab {
    box-shadow: rgb(221, 221, 221) 0px 1px 5px 1px;
    padding: 0px;
    transition: all 0.5s ease 0s;
    min-height: 40px;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>